import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { Link } from "../../../common/Link";
import { Trans } from "../../../common/Trans";
import { getLowestThresholdPlan } from "../../../utils/financing";
import { formatAPR, formatTermLength, money } from "../../../utils/format";
import { getDinero, getMonthlyPrice } from "../../../utils/money";
import { usePageURL } from "../../../utils/urls";

import styles from "./FinancingUpsellBlock.module.scss";

interface IProps {
    grandTotal: string;
}

export const FinancingUpsellBlock = (props: IProps) => {
    const grandTotal = getDinero(props.grandTotal);
    const lowestPlan = getLowestThresholdPlan();
    const homeURL = usePageURL("home");
    if (!lowestPlan) {
        return null;
    }
    // Already meet plan threshold?
    if (grandTotal.greaterThanOrEqual(lowestPlan.threshold)) {
        return null;
    }
    const orderTotalDelta = lowestPlan.threshold.subtract(grandTotal);
    const costPerMonth = getMonthlyPrice(
        lowestPlan.threshold,
        lowestPlan.length,
    );
    return (
        <div className={styles.root}>
            <p className={styles.headline}>
                {interpolate(
                    t`Special financing on orders of %(threshold)s or more!`,
                    { threshold: money(lowestPlan.threshold) },
                )}
            </p>
            <p className={styles.marginBottom}>
                {/* TODO(i18n): Hardcoded punctuation outside translation. Move period and superscript inside t string to allow proper localization of sentence structure */}
                {interpolate(
                    t`Add products totaling %(threshold)s or more to qualify for %(apr)s APR for %(length)s months`,
                    {
                        threshold: money(orderTotalDelta),
                        apr: formatAPR(lowestPlan.apr),
                        length: formatTermLength(lowestPlan.length),
                    },
                )}
                <sup>1</sup>.
            </p>
            <div className={styles.cta}>
                <div className={styles.ctaTitle}>
                    {t`Monthly Payments starting at:`}
                </div>
                <div className={styles.ctaCopy}>
                    {/* TODO(i18n): Hardcoded abbreviation "/mo." should be translatable. Different languages may use different abbreviations for "per month" */}
                    <Trans
                        fmtString={interpolate(
                            t`%(costPerMonth)s/mo.<Superscript1></Superscript1> for %(length)s months<Superscript2></Superscript2> `,
                            {
                                costPerMonth: `${money(costPerMonth)}`,
                                length: `${lowestPlan.length}`,
                            },
                        )}
                        data={{
                            Superscript1: () => <sup key="1">2</sup>,
                            Superscript2: () => (
                                <sup key="2">{lowestPlan.superscript}</sup>
                            ),
                        }}
                    />
                    {interpolate(t`(total payment of %(threshold)s)`, {
                        threshold: money(lowestPlan.threshold),
                    })}
                </div>
            </div>
            <p className={styles.link}>
                <Link href={homeURL}>{t`Continue Shopping`}</Link>
            </p>
        </div>
    );
};
